import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import axios from "axios";
import Select from "react-select";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { ImSearch, ImEye, ImCalendar } from "react-icons/im";
import moment from "moment";
import "moment/locale/es";
import flatpickr from "flatpickr";
import { Spanish } from "flatpickr/dist/l10n/es.js";
import "flatpickr/dist/themes/material_blue.css";
import "../Semaforo.css";

import Header from "../components/Header";
import Footer from "../components/Footer";
import ScrollToTop from "../components/ScrollTop";
import PhoneLoader from "../components/loader/PhoneLoader";
import { useAuth } from "../provider/authProvider";

moment.locale("es");

const PAGE_LIMIT = 30;

const formatQueryDate = (date) => {
    if (!date) return "";
    return moment(date).format("YYYY-MM-DD");
};

const getToday = () => moment().format("YYYY-MM-DD");
// const getMonthStart = () => moment().startOf("month").format("YYYY-MM-DD");
const getMonthStart = () => "2024-03-01";

const getSummaryPercent = (count, total) => {
    const base = Number(total || 0);
    if (base <= 0) return "0.00";
    return ((Number(count || 0) / base) * 100).toFixed(2);
};

const PAYMENT_STATE_OPTIONS = [
    { value: "ALL", label: "Todos los contratos" },
    { value: "PENDING", label: "Contratos pendientes" },
    { value: "FINISHED", label: "Contratos finalizados" },
];

const SEMAFORO_STATES = [
    {
        key: "ALL",
        category: "TODOS",
        code: "Todos",
        detail: "Todos los perfiles",
        color: "#111827",
        description: "Muestra todos los créditos clasificados por semáforo.",
    },
    {
        key: "MB",
        category: "MUY BUENO",
        code: "MB",
        detail: "EXCELENTE IDEAL",
        color: "#1f6eea",
        description: "Nunca fallan y no se atrasan en sus cuotas.",
    },
    {
        key: "B",
        category: "BUENO",
        code: "B",
        detail: "PERFIL NORMAL",
        color: "#34a853",
        description: "Solo se atrasaron 1 o 2 veces.",
    },
    {
        key: "R",
        category: "REGULAR",
        code: "R",
        detail: "CONSIDERABLE",
        color: "#fff200",
        description: "Se atrasaron de 3 a 5 veces.",
    },
    {
        key: "M",
        category: "MALO",
        code: "M",
        detail: "CON CUIDADO",
        color: "#ff7a00",
        description: "Terminaron contrato, pero se atrasaron más de 6 veces.",
    },
    {
        key: "MM",
        category: "MUY MALO",
        code: "MM",
        detail: "NEGATIVO",
        color: "#ff0000",
        description: "No terminaron contrato, pero pagaron más del 70%.",
    },
    {
        key: "P",
        category: "PÉRDIDA",
        code: "P",
        detail: "PÉRDIDA",
        color: "#8b1f0f",
        description: "Pagaron 69% o menos, o no pagaron ninguna cuota.",
    },
];

const currency = new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency: "PEN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

function KpiCard({ title, value, subValue, variant }) {
    return (
        <div className={`semaforoKpiCard ${variant || ""}`}>
            <span>{title}</span>
            <strong>{value}</strong>
            {subValue ? <small>{subValue}</small> : null}
        </div>
    );
}

function getStateConfig(key) {
    return SEMAFORO_STATES.find((item) => item.key === key) || SEMAFORO_STATES[0];
}

function formatDate(date) {
    if (!date) return "-";
    const formats = [
        "YYYY-MM-DD HH:mm:ss",
        "DD-MM-YYYY HH:mm:ss",
        "DD/MM/YYYY",
        "YYYY-MM-DD",
    ];
    const m = moment(date, formats, true);
    return m.isValid() ? m.format("DD/MM/YYYY hh:mm A") : date;
}

function StraightAnglePie({ value = 0, color = "#111827", label = "" }) {
    const safeValue = Math.max(0, Math.min(Number(value) || 0, 100));
    const data = [
        { name: "Avance", value: safeValue },
        { name: "Pendiente", value: 100 - safeValue },
    ];

    return (
        <div
            className="semaforoMiniChart"
            title={`${safeValue.toFixed(2)}% pagado`}
        >
            <ResponsiveContainer width="100%" height={58}>
                <PieChart>
                    <Pie
                        data={data}
                        startAngle={180}
                        endAngle={0}
                        cx="50%"
                        cy="85%"
                        innerRadius={22}
                        outerRadius={34}
                        paddingAngle={1}
                        dataKey="value"
                        stroke="none"
                    >
                        <Cell fill={color} />
                        <Cell fill="#e5e7eb" />
                    </Pie>
                </PieChart>
            </ResponsiveContainer>
            <div className="semaforoMiniChartText">
                <strong>{label}</strong>
                <span>{safeValue.toFixed(0)}%</span>
            </div>
        </div>
    );
}

function SemaforoPage(props) {
    const { user, rol } = useAuth();
    const loadMoreRef = useRef(null);
    const searchTimeoutRef = useRef(null);
    const calendarInputRef = useRef(null);
    const calendarInstanceRef = useRef(null);
    const firstDateLoadRef = useRef(true);

    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState([]);
    const [counters, setCounters] = useState({});
    const [kpis, setKpis] = useState({});
    const [managers, setManagers] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState("ALL");
    const [selectedManager, setSelectedManager] = useState({
        value: "ALL",
        label: "Todos los gestores",
    });
    const [selectedPaymentState, setSelectedPaymentState] = useState(
        PAYMENT_STATE_OPTIONS[0],
    );
    const [dateRange, setDateRange] = useState(() => [
        moment(getMonthStart(), "YYYY-MM-DD").toDate(),
        moment(getToday(), "YYYY-MM-DD").toDate(),
    ]);
    const [searchText, setSearchText] = useState("");
    const [totalRows, setTotalRows] = useState(0);
    const [nextOffset, setNextOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const managerOptions = useMemo(() => {
        return [{ value: "ALL", label: "Todos los gestores" }, ...managers];
    }, [managers]);

    const activeState = getStateConfig(selectedStatus);

    const normalizedSummary = useMemo(() => {
        if (summary && summary.length > 0) return summary;
        return SEMAFORO_STATES.map((state) => ({
            ...state,
            count: state.key === "ALL" ? counters.ALL || 0 : counters[state.key] || 0,
        }));
    }, [summary, counters]);

    const getSemaforoData = useCallback(
        async ({
            status = selectedStatus,
            manager = selectedManager,
            paymentState = selectedPaymentState,
            search = searchText,
            range = dateRange,
            offset = 0,
            append = false,
        } = {}) => {
            try {
                if (append) setLoadingMore(true);
                else setLoading(true);

                const resp = await axios({
                    method: "get",
                    url: process.env.REACT_APP_API + "/semaforo",
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                        user,
                    },
                    params: {
                        status,
                        managerId: manager?.value || "ALL",
                        paymentState: paymentState?.value || "ALL",
                        search: search || "",
                        startDate: range?.length === 2 ? formatQueryDate(range[0]) : "",
                        endDate: range?.length === 2 ? formatQueryDate(range[1]) : "",
                        limit: PAGE_LIMIT,
                        offset,
                    },
                });

                const resData = resp.data;
                if (resData?.msg === "success") {
                    setRows((prev) =>
                        append ? [...prev, ...(resData.data || [])] : resData.data || [],
                    );
                    setSummary(resData.summary || []);
                    setCounters(resData.counters || {});
                    setKpis(resData.kpis || {});
                    setManagers(resData.managers || []);
                    setTotalRows(Number(resData.total || 0));
                    setNextOffset(Number(resData.nextOffset || 0));
                    setHasMore(Boolean(resData.hasMore));
                } else if (!append) {
                    setRows([]);
                    setTotalRows(0);
                    setNextOffset(0);
                    setHasMore(false);
                    setKpis({});
                }
            } catch (err) {
                console.log(err);
                if (!append) {
                    setRows([]);
                    setTotalRows(0);
                    setNextOffset(0);
                    setHasMore(false);
                    setKpis({});
                }
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        },
        [
            selectedStatus,
            selectedManager,
            selectedPaymentState,
            searchText,
            dateRange,
            user,
        ],
    );

    const resetAndLoad = useCallback(
        (payload = {}) => {
            setRows([]);
            setNextOffset(0);
            setHasMore(true);
            getSemaforoData({ ...payload, offset: 0, append: false });
        },
        [getSemaforoData],
    );

    const onStatusChange = (status) => {
        setSelectedStatus(status);
        resetAndLoad({ status });
    };

    const onManagerChange = (option) => {
        setSelectedManager(option);
        resetAndLoad({ manager: option });
    };

    const onPaymentStateChange = (option) => {
        setSelectedPaymentState(option);
        resetAndLoad({ paymentState: option });
    };

    const onDateRangeChange = useCallback((selectedDates) => {
        if (!selectedDates || selectedDates.length === 0) {
            setDateRange([]);
            return;
        }

        if (selectedDates.length === 2) {
            setDateRange(selectedDates);
        }
    }, []);

    const resetDateRange = () => {
        const defaultRange = [getMonthStart(), getToday()];
        if (calendarInstanceRef.current) {
            calendarInstanceRef.current.setDate(defaultRange, true);
            return;
        }
        setDateRange(
            defaultRange.map((date) => moment(date, "YYYY-MM-DD").toDate()),
        );
    };

    const onSearchChange = (event) => {
        const value = event.target.value;
        setSearchText(value);

        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

        if (value.length === 0 || value.length >= 3) {
            searchTimeoutRef.current = setTimeout(() => {
                resetAndLoad({ search: value });
            }, 350);
        }
    };

    useEffect(() => {
        getSemaforoData({ offset: 0, append: false });
        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (firstDateLoadRef.current) {
            firstDateLoadRef.current = false;
            return;
        }

        resetAndLoad({ range: dateRange });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange]);

    useEffect(() => {
        flatpickr.localize(Spanish);

        if (calendarInputRef.current && !calendarInstanceRef.current) {
            const defaultRange = [getMonthStart(), getToday()];

            calendarInstanceRef.current = flatpickr(calendarInputRef.current, {
                mode: "range",
                dateFormat: "Y-m-d",
                locale: Spanish,
                allowInput: true,
                closeOnSelect: false,
                defaultDate: defaultRange,
                onChange: onDateRangeChange,
            });
        }

        return () => {
            try {
                calendarInstanceRef.current?.destroy?.();
            } catch (error) {
                console.warn("flatpickr semaforo destroy error:", error);
            }
            calendarInstanceRef.current = null;
        };
    }, [onDateRangeChange]);

    useEffect(() => {
        const target = loadMoreRef.current;
        if (!target) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                const firstEntry = entries[0];
                if (firstEntry.isIntersecting && hasMore && !loading && !loadingMore) {
                    getSemaforoData({ offset: nextOffset, append: true });
                }
            },
            { root: null, rootMargin: "260px", threshold: 0.1 },
        );

        observer.observe(target);
        return () => observer.disconnect();
    }, [getSemaforoData, hasMore, loading, loadingMore, nextOffset]);

    const financeGrowth = Number(kpis.totalFinanceGrowth || 0);

    return (
        <div>
            <Helmet>
                <title>Semáforo Crediticio | MovilesPay</title>
            </Helmet>

            {loading && <PhoneLoader />}
            <ScrollToTop />
            <Header user={user} namePage={props.namePage || "Semáforo Crediticio"} />

            <section className="sectionBody sectionBodySemaforo">
                <div className="containerCustom containerBody">
                    <div className="blockBody">
                        <div className="semaforoHeader">
                            <div>
                                <span className="semaforoEyebrow">Créditos MovilesPay</span>
                                <h2>Semáforo Crediticio del Cliente</h2>
                                <p>
                                    Clasificación profesional del cliente según cumplimiento de
                                    cuotas, pagos registrados y reprogramaciones.
                                </p>
                            </div>

                            <div
                                className="semaforoActiveBadge"
                                style={{ borderColor: activeState.color }}
                            >
                                <span style={{ background: activeState.color }} />
                                {activeState.category}
                            </div>
                        </div>

                        <div className="semaforoStateTableWrap">
                            <table className="semaforoStateTable">
                                <thead>
                                    <tr>
                                        <th>Item</th>
                                        <th>Categoría</th>
                                        <th>Código y colores</th>
                                        <th>Detalle</th>
                                        <th>Descripción</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {SEMAFORO_STATES.map((state, index) => (
                                        <tr
                                            key={state.key}
                                            onClick={() => onStatusChange(state.key)}
                                            className={selectedStatus === state.key ? "active" : ""}
                                            style={{ "--state-color": state.color }}
                                        >
                                            <td>{index === 0 ? "-" : index}</td>
                                            <td>{state.category}</td>
                                            <td>
                                                <span
                                                    className="semaforoCode"
                                                    style={{ background: state.color }}
                                                >
                                                    {state.code}
                                                </span>
                                            </td>
                                            <td>{state.detail}</td>
                                            <td>{state.description}</td>
                                            <td>
                                                {state.key === "ALL"
                                                    ? counters.ALL || 0
                                                    : counters[state.key] || 0}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="semaforoFiltersGrid">
                            <div className="semaforoSearchBox">
                                <ImSearch />
                                <input
                                    type="text"
                                    value={searchText}
                                    onChange={onSearchChange}
                                    placeholder="Buscar por nombre completo o DNI..."
                                />
                            </div>

                            <div className="semaforoDateBox">
                                <input
                                    ref={calendarInputRef}
                                    type="text"
                                    className="semaforoDateInput"
                                    placeholder="Filtrar por fecha de contrato"
                                    readOnly
                                />
                                <ImCalendar style={{ fontSize: 24 }} />
                            </div>

                            {rol !== "3" && (
                                <div className="semaforoSelectBox">
                                    <Select
                                        value={selectedManager}
                                        options={managerOptions}
                                        onChange={onManagerChange}
                                        placeholder="Filtrar por gestor"
                                        classNamePrefix="semaforoSelect"
                                        isSearchable
                                    />
                                </div>
                            )}

                            <div className="semaforoSelectBox">
                                <Select
                                    value={selectedPaymentState}
                                    options={PAYMENT_STATE_OPTIONS}
                                    onChange={onPaymentStateChange}
                                    placeholder="Filtrar por contrato"
                                    classNamePrefix="semaforoSelect"
                                    isSearchable={false}
                                />
                            </div>
                        </div>

                        <div className="semaforoResumeGrid">
                            {normalizedSummary.map((item) => (
                                <button
                                    type="button"
                                    className={
                                        selectedStatus === item.key
                                            ? "semaforoResumeCard active"
                                            : "semaforoResumeCard"
                                    }
                                    key={item.key}
                                    style={{ "--state-color": item.color }}
                                    onClick={() => onStatusChange(item.key)}
                                    title={`Filtrar por ${item.category}`}
                                >
                                    <span>{item.key === "ALL" ? "Todos" : item.key}</span>
                                    <strong>
                                        {item.count || 0}
                                        <small>
                                            ({getSummaryPercent(item.count, counters.ALL)}%)
                                        </small>
                                    </strong>
                                    <p>{item.category}</p>
                                </button>
                            ))}
                        </div>

                        <div className="semaforoKpiGrid">
                            <KpiCard
                                title="Precio total de equipos"
                                value={currency.format(Number(kpis.totalPhonePrice || 0))}
                                subValue="Suma de precios de equipos financiados"
                                variant="purple"
                            />
                            <KpiCard
                                title="Total iniciales"
                                value={currency.format(Number(kpis.totalInitialPrice || 0))}
                                subValue="Suma de iniciales de equipos financiados"
                                variant="green"
                            />
                            <KpiCard
                                title="Monto Financiado + Intereses"
                                value={currency.format(Number(kpis.totalFinanceAmount || 0))}
                                subValue={`Crecimiento: ${financeGrowth.toFixed(2)}%`}
                                variant="greenSoft"
                            />
                            <KpiCard
                                title="Monto cancelado"
                                value={currency.format(Number(kpis.paidAmount || 0))}
                                subValue={`${Number(kpis.paidInstallments || 0)} cuotas canceladas · ${Number(kpis.paidPercent || 0).toFixed(2)}% de la cartera`}
                                variant="blue"
                            />
                            <KpiCard
                                title="Monto pendiente a cobrar"
                                value={currency.format(Number(kpis.pendingAmount || 0))}
                                subValue={`${Number(kpis.pendingInstallments || 0)} cuotas pendientes · ${Number(kpis.pendingPercent || 0).toFixed(2)}% de la cartera`}
                                variant="redSoft"
                            />
                            <KpiCard
                                title="Mora pendiente a cobrar"
                                value={currency.format(Number(kpis.overdueAmount || 0))}
                                subValue={`${Number(kpis.overdueInstallments || 0)} cuotas vencidas · ${Number(kpis.overduePercent || 0).toFixed(2)}% de la cartera`}
                                variant="red"
                            />
                            <KpiCard
                                title="Ticket promedio"
                                value={currency.format(Number(kpis.avgTicket || 0))}
                                subValue="Promedio por crédito"
                                variant="orange"
                            />
                        </div>

                        <div className="semaforoTableTitle">
                            <h3>Lista de créditos clasificados</h3>
                            <span>
                                Mostrando {rows.length} de {totalRows} resultado(s)
                            </span>
                        </div>

                        <div className="semaforoClientTableWrap">
                            <table className="semaforoClientTable">
                                <thead>
                                    <tr>
                                        <th>Estado</th>
                                        <th>Contrato</th>
                                        <th>Crédito</th>
                                        <th>Cliente</th>
                                        <th>DNI</th>
                                        <th>Celular</th>
                                        <th>Equipo</th>
                                        <th>Sede</th>
                                        <th>Asesor venta</th>
                                        <th>Gestor aprobó</th>
                                        <th>Cuotas</th>
                                        <th>Atrasos</th>
                                        <th>Reprogram.</th>
                                        <th>Mora</th>
                                        <th>Fecha contrato</th>
                                        <th>Ver</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 && !loading && (
                                        <tr>
                                            <td colSpan="16" className="semaforoEmpty">
                                                No se encontraron créditos con el filtro seleccionado.
                                            </td>
                                        </tr>
                                    )}

                                    {rows.map((item) => (
                                        <tr
                                            key={item.creditId}
                                            className={`semaforoRow semaforoRow${item.statusKey}`}
                                        >
                                            <td className="semaforoStatusTd">
                                                <div className="semaforoStatusContent">
                                                    <span
                                                        className="semaforoCode small"
                                                        style={{ background: item.color }}
                                                    >
                                                        {item.statusKey}
                                                    </span>
                                                    <div>
                                                        <strong>{item.category}</strong>
                                                        <p>{item.detail}</p>
                                                    </div>
                                                    <StraightAnglePie
                                                        value={item.paidPercent}
                                                        color={item.color}
                                                        label={item.statusKey}
                                                    />
                                                </div>
                                            </td>
                                            <td>
                                                <span
                                                    className={
                                                        Number(item.paymentState) === 2
                                                            ? "semaforoContractBadge finished"
                                                            : "semaforoContractBadge pending"
                                                    }
                                                >
                                                    {item.paymentStateText || "Pendiente"}
                                                </span>
                                            </td>
                                            <td>#{item.creditId}</td>
                                            <td className="semaforoClientName">{item.clientName}</td>
                                            <td>{item.dni}</td>
                                            <td>{item.phoneNumber || "-"}</td>
                                            <td>{item.phoneName || "-"}</td>
                                            <td>{item.sedeName || "-"}</td>
                                            <td>{item.advisorName || "Sin asesor"}</td>
                                            <td>{item.managerName || "Sin gestor"}</td>
                                            <td>
                                                <strong>
                                                    {item.paidFees}/{item.totalFees}
                                                </strong>
                                                <small>
                                                    {Number(item.paidPercent || 0).toFixed(2)}% pagado
                                                </small>
                                            </td>
                                            <td>{item.latePayments}</td>
                                            <td>{item.rescheduleCount}</td>
                                            <td>
                                                <strong>{item.overdueFees}</strong>
                                                <small>
                                                    {currency.format(item.overdueAmount || 0)}
                                                </small>
                                            </td>
                                            <td>{formatDate(item.contractDate)}</td>
                                            <td>
                                                {rol !== "3" ? (
                                                    <Link
                                                        className="semaforoViewBtn"
                                                        to={`../credit/${item.creditId}`}
                                                    >
                                                        <ImEye />
                                                    </Link>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div ref={loadMoreRef} className="semaforoLazyLoadTrigger">
                            {loadingMore && <span>Cargando más créditos...</span>}
                            {!hasMore && rows.length > 0 && (
                                <span>No hay más créditos por cargar.</span>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}

export default SemaforoPage;
